// Content Types and Interfaces for 1vX.gg
// Based on GraphQL schema and route implementations

// Base content interfaces
export interface Author {
  displayName: string;
  slug: string;
  bioBlurb?: string;
  profilePicture?: {
    url: string;
    description?: string;
  };
  socialLinks?: SocialLink[];
}

export interface SocialLink {
  url: string;
  platform: string;
  accessibilityDescription?: string;
}

export interface CoverImage {
  url: string;
  description?: string;
}

export interface RichText {
  json: unknown; // Rich text JSON format from Contentful
}

// Article types
export interface BaseArticle {
  title: string;
  slug: string;
  shortDescription: string;
  publishedDate: string;
  body?: string | RichText;
  content?: RichText;
  coverImage?: CoverImage;
  author?: Author;
  relatedArticles?: RelatedArticle[];
}

export interface GameArticle extends BaseArticle {
  __typename: 'GameArticle';
}

export interface ESportsArticle extends BaseArticle {
  __typename: 'ESportsArticle';
}

export interface ReviewArticle extends BaseArticle {
  __typename: 'ReviewArticle';
}

export type Article = GameArticle | ESportsArticle | ReviewArticle;

export interface RelatedArticle {
  __typename: 'GameArticle' | 'ESportsArticle' | 'ReviewArticle';
  title: string;
  shortDescription: string;
  slug: string;
  publishedDate: string;
  coverImage?: CoverImage;
  author?: {
    displayName: string;
    profilePicture?: {
      url: string;
    };
  };
}

// Game-specific types
export interface Game {
  title: string;
  slug: string;
  description?: string;
  coverImage?: CoverImage;
  screenshots?: Screenshot[];
  genre?: string;
  platform?: string;
  releaseDate?: string;
  developer?: string;
  publisher?: string;
  rating?: string;
  relatedArticles?: RelatedArticle[];
}

export interface Screenshot {
  url: string;
  description?: string;
}

// Streamer-specific types
export interface Streamer {
  displayName: string;
  slug: string;
  avatar?: CoverImage;
  bannerImage?: CoverImage;
  bio?: string;
  country?: string;
  isLive?: boolean;
  followers?: number;
  totalViews?: number;
  twitchHandle?: string;
  youtubeHandle?: string;
  twitterHandle?: string;
  instagramHandle?: string;
  favouriteGames?: Game[];
  recentStreams?: Stream[];
  scheduleItems?: ScheduleItem[];
}

export interface Stream {
  title: string;
  date: string;
  duration?: number;
  viewers?: number;
  game?: Game;
  thumbnail?: CoverImage;
}

export interface ScheduleItem {
  date: string;
  game?: Game;
  description?: string;
}

// GraphQL collection response types
export interface CollectionResponse<T> {
  items: T[];
}

export interface ArticleCollection extends CollectionResponse<Article> {}
export interface GameCollection extends CollectionResponse<Game> {}
export interface StreamerCollection extends CollectionResponse<Streamer> {}
export interface StreamCollection extends CollectionResponse<Stream> {}
export interface ScheduleCollection extends CollectionResponse<ScheduleItem> {}

// GraphQL query response types
export interface NewsIndexResponse {
  articles: ArticleCollection;
}

export interface GamesIndexResponse {
  articles: ArticleCollection;
}

export interface ReviewsIndexResponse {
  articles: ArticleCollection;
}

export interface ESportsIndexResponse {
  articles: ArticleCollection;
}

export interface GamePostResponse {
  article: ArticleCollection;
}

export interface GameResponse {
  game: GameCollection;
}

export interface StreamersResponse {
  streamers: StreamerCollection;
}

export interface StreamerResponse {
  streamer: StreamerCollection;
}

// Route model types (what routes return)
export interface RouteModel<T> {
  data: T;
  loading: boolean;
  error?: Error;
}

export type NewsIndexModel = Article[];
export type GamesIndexModel = Article[];
export type ReviewsIndexModel = Article[];
export type ESportsIndexModel = Article[];
export type GamePostModel = Article;
export type GameModel = Game;
export type StreamersModel = {
  streamers: Streamer[];
  gameSlug: string;
};
export type StreamerModel = Streamer;

// Featured block types (used in home page)
export interface FeaturedBlock {
  slug: string;
  articles: ArticleCollection;
}

export interface HomePageResponse {
  homePage: CollectionResponse<FeaturedBlock>;
  topNews: CollectionResponse<FeaturedBlock>;
  newsSlider: CollectionResponse<FeaturedBlock>;
  popularPosts: CollectionResponse<FeaturedBlock>;
}

// Query variable types
export interface SlugQueryVariables {
  slug: string;
}

export interface GameSlugQueryVariables {
  gameSlug: string;
}

export interface StreamerSlugQueryVariables {
  slug: string;
}

// GraphQL query types
export type QueryVariables =
  | SlugQueryVariables
  | GameSlugQueryVariables
  | StreamerSlugQueryVariables
  | Record<string, never>; // for queries with no variables

// Utility types for form handling
export interface FormField<T = string> {
  value: T;
  isValid: boolean;
  errors: string[];
  touched: boolean;
}

export interface ContentForm {
  title: FormField;
  slug: FormField;
  shortDescription: FormField;
  content: FormField;
  publishedDate: FormField;
  author: FormField;
  coverImage: FormField;
}

// Content status types
export type ContentStatus = 'draft' | 'published' | 'archived' | 'scheduled';

export interface ContentMeta {
  id: string;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  version: number;
  locale?: string;
}
